import { type GoalState } from "../buildRequestTemplate";

/** Mirrors the server's ``_TRIAGE_LABEL_PATTERN``. Labels become Roboto
 *  tags and Bedrock-tool enum values, so the character set is deliberately
 *  conservative. */
export const TRIAGE_LABEL_PATTERN = /^[a-zA-Z0-9_-]+$/;
/** Mirror of ``_MAX_TRIAGE_LABELS`` on the backend. */
export const MAX_TRIAGE_LABELS = 50;
/** Mirror of ``_MAX_TRIAGE_DESCRIPTION_CHARS`` on the backend. */
export const MAX_TRIAGE_DESCRIPTION_CHARS = 500;
/** Mirror of ``DatasetSummaryAgentGoal.summary_format_spec_prompt`` upper bound. */
export const MAX_SUMMARY_FORMAT_CHARS = 4000;
/** Mirror of ``_MAX_EVENT_FOCUS_PROMPT_CHARS`` on the backend. */
export const MAX_EVENT_FOCUS_PROMPT_CHARS = 4000;
/** Mirror of ``_MAX_EVENT_VOCABULARY`` on the backend. */
export const MAX_EVENT_VOCABULARY = 50;
/** Mirror of ``_MAX_TAG_VOCABULARY`` on the backend. */
export const MAX_TAG_VOCABULARY = 50;
/** Mirror of ``_MAX_VOCABULARY_KEY_CHARS`` on the backend. */
export const MAX_VOCABULARY_KEY_CHARS = 100;
/** Mirror of ``_MAX_VOCABULARY_DESCRIPTION_CHARS`` on the backend. */
export const MAX_VOCABULARY_DESCRIPTION_CHARS = 500;

export interface TriageRowError {
  /** Index into ``label_vocabulary``. ``-1`` means the error is goal-level
   *  (e.g. "at least one entry required") rather than tied to a row. */
  index: number;
  /** Which field on the row failed. ``"row"`` for goal-level errors. */
  field: "label" | "description" | "row";
  message: string;
}

export interface VocabRowError {
  /** Index into the vocabulary array. ``-1`` means the error is goal-level
   *  (e.g. "at least one entry required") rather than tied to a row. */
  index: number;
  /** Which field on the row failed. ``"key"`` is the event-name / tag cell;
   *  ``"row"`` is a goal-level error. */
  field: "key" | "description" | "row";
  message: string;
}

export interface SummaryGoalErrors {
  /** Validation messages for the summary-format textarea. Empty if valid. */
  summary_format_spec_prompt: string[];
}

export interface TriageGoalErrors {
  /** Per-row + goal-level errors. Empty if valid. */
  rows: TriageRowError[];
}

export interface CreateEventsGoalErrors {
  /** Per-row + goal-level errors for the event-type vocabulary. Empty if valid. */
  event_vocabulary: VocabRowError[];
  /** Per-row + goal-level errors for the tag vocabulary. Empty if valid. */
  tag_vocabulary: VocabRowError[];
  /** Validation messages for the event-focus textarea. Empty if valid. */
  event_focus_prompt: string[];
}

/** Pure validator for a single goal. The returned union is discriminated
 *  by the shape of the error object, mirroring the three ``GoalState``
 *  variants. */
export function validateGoal(
  goal: GoalState,
): SummaryGoalErrors | TriageGoalErrors | CreateEventsGoalErrors {
  if (goal.goal_type === "dataset_summary") {
    return validateSummaryGoal(goal);
  }
  if (goal.goal_type === "dataset_triage") {
    return validateTriageGoal(goal);
  }
  return validateCreateEventsGoal(goal);
}

function validateSummaryGoal(goal: {
  summary_format_spec_prompt: string;
}): SummaryGoalErrors {
  const errors: string[] = [];
  // Cap is enforced on raw length to mirror the server's pydantic
  // ``max_length=4000`` (which counts whitespace). Whitespace-only input is
  // treated as "use default" elsewhere, so we don't flag it as required here,
  // but we still reject overflow regardless of trim state.
  if (goal.summary_format_spec_prompt.length > MAX_SUMMARY_FORMAT_CHARS) {
    errors.push(
      `Summary format hint must be at most ${MAX_SUMMARY_FORMAT_CHARS} characters.`,
    );
  }
  return { summary_format_spec_prompt: errors };
}

function validateTriageGoal(goal: {
  label_vocabulary: Array<{ label: string; description: string }>;
}): TriageGoalErrors {
  const rows: TriageRowError[] = [];
  if (goal.label_vocabulary.length === 0) {
    rows.push({
      index: -1,
      field: "row",
      message: "Add at least one label.",
    });
    return { rows };
  }
  if (goal.label_vocabulary.length > MAX_TRIAGE_LABELS) {
    rows.push({
      index: -1,
      field: "row",
      message: `At most ${MAX_TRIAGE_LABELS} labels per goal.`,
    });
  }
  const seenLabels = new Map<string, number>();
  goal.label_vocabulary.forEach((row, idx) => {
    if (row.label.trim() === "") {
      rows.push({
        index: idx,
        field: "label",
        message: "Label is required.",
      });
    } else if (!TRIAGE_LABEL_PATTERN.test(row.label)) {
      rows.push({
        index: idx,
        field: "label",
        message:
          "Use only letters, digits, underscores, or hyphens (no spaces).",
      });
    } else {
      const previousIdx = seenLabels.get(row.label);
      if (previousIdx !== undefined) {
        rows.push({
          index: idx,
          field: "label",
          message: `Duplicate of label at row ${previousIdx + 1}.`,
        });
      } else {
        seenLabels.set(row.label, idx);
      }
    }
    if (row.description.trim() === "") {
      rows.push({
        index: idx,
        field: "description",
        message: "Describe what this label signifies.",
      });
    } else if (row.description.length > MAX_TRIAGE_DESCRIPTION_CHARS) {
      rows.push({
        index: idx,
        field: "description",
        message: `Description must be at most ${MAX_TRIAGE_DESCRIPTION_CHARS} characters.`,
      });
    }
  });
  return { rows };
}

/**
 * Shared row validator for the create-events goal's two vocabularies. Each
 * row pairs a key (event name / tag) with a description; the key must be
 * non-empty, length-capped, and unique within the vocabulary, and the
 * description non-empty and length-capped. ``requireAtLeastOne`` adds a
 * goal-level error for an empty vocabulary (event types are required; tags
 * are not).
 */
function validateVocabularyRows(
  rows: Array<{ key: string; description: string }>,
  options: { requireAtLeastOne: boolean; max: number; emptyMessage: string },
): VocabRowError[] {
  const errors: VocabRowError[] = [];
  if (rows.length === 0) {
    if (options.requireAtLeastOne) {
      errors.push({ index: -1, field: "row", message: options.emptyMessage });
    }
    return errors;
  }
  if (rows.length > options.max) {
    errors.push({
      index: -1,
      field: "row",
      message: `At most ${options.max} entries.`,
    });
  }
  const seenKeys = new Map<string, number>();
  rows.forEach((row, idx) => {
    if (row.key.trim() === "") {
      errors.push({ index: idx, field: "key", message: "Required." });
    } else if (row.key.length > MAX_VOCABULARY_KEY_CHARS) {
      errors.push({
        index: idx,
        field: "key",
        message: `Must be at most ${MAX_VOCABULARY_KEY_CHARS} characters.`,
      });
    } else {
      const previousIdx = seenKeys.get(row.key);
      if (previousIdx !== undefined) {
        errors.push({
          index: idx,
          field: "key",
          message: `Duplicate of row ${previousIdx + 1}.`,
        });
      } else {
        seenKeys.set(row.key, idx);
      }
    }
    if (row.description.trim() === "") {
      errors.push({
        index: idx,
        field: "description",
        message: "Describe when this applies.",
      });
    } else if (row.description.length > MAX_VOCABULARY_DESCRIPTION_CHARS) {
      errors.push({
        index: idx,
        field: "description",
        message: `Description must be at most ${MAX_VOCABULARY_DESCRIPTION_CHARS} characters.`,
      });
    }
  });
  return errors;
}

function validateCreateEventsGoal(goal: {
  event_vocabulary: Array<{ name: string; description: string }>;
  tag_vocabulary: Array<{ tag: string; description: string }>;
  event_focus_prompt: string;
}): CreateEventsGoalErrors {
  const eventVocabularyErrors = validateVocabularyRows(
    goal.event_vocabulary.map((row) => ({
      key: row.name,
      description: row.description,
    })),
    {
      requireAtLeastOne: true,
      max: MAX_EVENT_VOCABULARY,
      emptyMessage: "Add at least one event type.",
    },
  );
  const tagVocabularyErrors = validateVocabularyRows(
    goal.tag_vocabulary.map((row) => ({
      key: row.tag,
      description: row.description,
    })),
    { requireAtLeastOne: false, max: MAX_TAG_VOCABULARY, emptyMessage: "" },
  );
  const focusErrors: string[] = [];
  // Length cap mirrors the server's pydantic ``max_length`` (counts
  // whitespace). A blank prompt is valid — the vocabularies steer the agent
  // on their own — so only overflow is flagged.
  if (goal.event_focus_prompt.length > MAX_EVENT_FOCUS_PROMPT_CHARS) {
    focusErrors.push(
      `Event focus must be at most ${MAX_EVENT_FOCUS_PROMPT_CHARS} characters.`,
    );
  }
  return {
    event_vocabulary: eventVocabularyErrors,
    tag_vocabulary: tagVocabularyErrors,
    event_focus_prompt: focusErrors,
  };
}

/** Convenience: true when no validator on any goal would block a save.
 *  Note: returns ``true`` for an empty array — the server requires at least
 *  one goal, so use ``canSubmit`` elsewhere to enforce ``>=1``. */
export function allGoalsValid(goals: GoalState[]): boolean {
  return goals.every((goal) => {
    const errors = validateGoal(goal);
    if ("summary_format_spec_prompt" in errors) {
      return errors.summary_format_spec_prompt.length === 0;
    }
    if ("rows" in errors) {
      return errors.rows.length === 0;
    }
    return (
      errors.event_vocabulary.length === 0 &&
      errors.tag_vocabulary.length === 0 &&
      errors.event_focus_prompt.length === 0
    );
  });
}
